import { type ReactNode } from "react";
import { PostHogProvider } from "@posthog/react";
import { getEnvVar } from "./env";

// PostHog (product analytics, session replay, error tracking, feature flags).
// Publishable project token + host, read at runtime (window.__ENV__ in Docker)
// or build time (import.meta.env in dev) via getEnvVar.
const posthogKey = getEnvVar("VITE_POSTHOG_KEY", "");
const posthogHost = getEnvVar("VITE_POSTHOG_HOST", "https://eu.i.posthog.com");

// Stamped on every event/replay/exception so telemetry is filterable by release/env.
const appEnvironment = getEnvVar("VITE_APP_ENV", import.meta.env.MODE);
const appVersion = __APP_VERSION__;

// Telemetry is whitelisted to staging/production (matches the backend).
const telemetryEnabled = appEnvironment === "staging" || appEnvironment === "production";

// Backend hostname for tracing_headers: posthog-js auto-adds X-POSTHOG-SESSION-ID
// to API calls to this host so the backend can link logs/exceptions to the replay.
const backendHost = (() => {
  try {
    return new URL(getEnvVar("VITE_API_URL", "http://localhost:3001")).hostname;
  } catch {
    return undefined;
  }
})();

/**
 * Wraps children in PostHogProvider when a key is configured (otherwise a clean
 * no-op, so the shared GlobalErrorBoundary's captureException does nothing).
 */
export function PostHogWrapper({ children }: { children: ReactNode }) {
  if (!posthogKey || !telemetryEnabled) return <>{children}</>;
  return (
    <PostHogProvider
      apiKey={posthogKey}
      options={{
        api_host: posthogHost,
        defaults: "2026-05-30",
        // Session replay. Inputs are masked by default; keep password + email
        // masked explicitly. Add `.ph-no-capture` to any sensitive element.
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: { password: true, email: true },
        },
        enable_recording_console_log: true,
        ...(backendHost ? { tracing_headers: [backendHost] } : {}),
        loaded: (ph) => ph.register({ environment: appEnvironment, app_version: appVersion }),
      }}
    >
      {children}
    </PostHogProvider>
  );
}
